import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User,
  Building2,
  Wrench,
  Truck,
  Shield,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Camera,
  Save,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Award,
  Clock,
  Briefcase,
  Globe,
  Lock,
  Edit2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

// Role icons and colors
const ROLE_CONFIG = {
  hospital: { icon: Building2, color: "from-blue-600 to-cyan-500", label: "Hospital Administrator" },
  contractor: { icon: Wrench, color: "from-emerald-600 to-teal-500", label: "Service Contractor" },
  supplier: { icon: Truck, color: "from-amber-500 to-orange-500", label: "Supplier Partner" },
  admin: { icon: Shield, color: "from-violet-600 to-purple-500", label: "Administrator" },
  super_admin: { icon: Shield, color: "from-red-600 to-pink-500", label: "Super Administrator" },
  hub_admin: { icon: Shield, color: "from-indigo-600 to-blue-500", label: "Hub Administrator" }
};

// Role-specific profile fields
const ROLE_FIELDS = {
  hospital: [
    { key: "facility_name", label: "Facility Name", type: "text", icon: Building2 },
    { key: "department", label: "Department Code", type: "text", icon: Briefcase },
    { key: "delivery_instructions", label: "Delivery Instructions", type: "textarea", icon: MapPin }
  ],
  contractor: [
    { key: "skills", label: "Skills & Specializations", type: "textarea", icon: Wrench },
    { key: "service_radius", label: "Service Radius (miles)", type: "number", icon: Globe },
    { key: "insurance_expiry", label: "Insurance Expiry Date", type: "date", icon: Calendar },
    { key: "license_number", label: "License Number", type: "text", icon: FileText }
  ],
  supplier: [
    { key: "catalog_url", label: "Product Catalog URL", type: "url", icon: Globe },
    { key: "warehouse_location", label: "Warehouse Location", type: "text", icon: MapPin },
    { key: "tax_id", label: "Tax ID / EIN", type: "text", icon: FileText }
  ],
  admin: [
    { key: "admin_level", label: "Admin Level", type: "text", icon: Shield, readOnly: true },
    { key: "access_scope", label: "Access Scope", type: "text", icon: Lock, readOnly: true }
  ],
  super_admin: [
    { key: "admin_level", label: "Admin Level", type: "text", icon: Shield, readOnly: true },
    { key: "access_scope", label: "Access Scope", type: "text", icon: Lock, readOnly: true }
  ]
};

export default function AccountProfilePage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  // Determine user role
  const userRole = user?.role || "hospital";
  const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG.hospital;
  const RoleIcon = roleConfig.icon;
  const roleFields = ROLE_FIELDS[userRole] || ROLE_FIELDS.hospital;

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    if (!token) {
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/account/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData(data);
      } else {
        // If endpoint doesn't exist yet, use user data
        setProfile(user);
        setFormData(user || {});
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfile(user);
      setFormData(user || {});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/account/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user || formData);
        setEditMode(false);
        toast.success("Profile updated successfully!");
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Error saving profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/90 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="font-bold text-lg">My Profile</h1>
                <p className="text-xs text-slate-400">{roleConfig.label}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFormData(profile);
                      setEditMode(false);
                    }}
                    className="text-slate-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setEditMode(true)}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Header Card */}
        <div className={`bg-gradient-to-r ${roleConfig.color} rounded-2xl p-6 mb-8`}>
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl bg-white/20 flex items-center justify-center overflow-hidden">
                {profile?.profile_image ? (
                  <img src={profile.profile_image} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-white/80" />
                )}
              </div>
              {editMode && (
                <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Camera className="w-4 h-4 text-slate-700" />
                </button>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <RoleIcon className="w-5 h-5 text-white/80" />
                <span className="text-white/80 text-sm">{roleConfig.label}</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {profile?.name || formData?.name || "User"}
              </h2>
              <p className="text-white/70 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {profile?.email || formData?.email}
              </p>
            </div>

            {/* Status Badge */}
            <div className="text-right">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                profile?.status === "active" || !profile?.status
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-amber-500/20 text-amber-300"
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  profile?.status === "active" || !profile?.status
                    ? "bg-emerald-400"
                    : "bg-amber-400"
                }`}></div>
                {profile?.status === "active" || !profile?.status ? "Active" : profile?.status}
              </div>
              <p className="text-white/50 text-xs mt-2">
                Member since {new Date(profile?.created_at || Date.now()).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            Basic Information
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Full Name</Label>
              {editMode ? (
                <Input
                  value={formData.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="mt-1 bg-slate-900 border-slate-700"
                />
              ) : (
                <p className="mt-1 text-white">{profile?.name || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-400">Email Address</Label>
              <p className="mt-1 text-white flex items-center gap-2">
                {profile?.email || "—"}
                <Lock className="w-3 h-3 text-slate-500" title="Cannot be changed" />
              </p>
            </div>
            <div>
              <Label className="text-slate-400">Phone Number</Label>
              {editMode ? (
                <Input
                  value={formData.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="mt-1 bg-slate-900 border-slate-700"
                  placeholder="(XXX) XXX-XXXX"
                />
              ) : (
                <p className="mt-1 text-white">{profile?.phone || "—"}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-400">Company / Organization</Label>
              {editMode ? (
                <Input
                  value={formData.company || ""}
                  onChange={(e) => handleChange("company", e.target.value)}
                  className="mt-1 bg-slate-900 border-slate-700"
                />
              ) : (
                <p className="mt-1 text-white">{profile?.company || "—"}</p>
              )}
            </div>
          </div>
        </div>

        {/* Role-Specific Fields */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <RoleIcon className="w-5 h-5 text-cyan-400" />
            {roleConfig.label} Details
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {roleFields.map((field) => {
              const FieldIcon = field.icon;
              return (
                <div key={field.key} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                  <Label className="text-slate-400 flex items-center gap-2">
                    <FieldIcon className="w-4 h-4" />
                    {field.label}
                  </Label>
                  {editMode && !field.readOnly ? (
                    field.type === "textarea" ? (
                      <Textarea
                        value={formData[field.key] || ""}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="mt-1 bg-slate-900 border-slate-700"
                        rows={3}
                      />
                    ) : (
                      <Input
                        type={field.type}
                        value={formData[field.key] || ""}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="mt-1 bg-slate-900 border-slate-700"
                      />
                    )
                  ) : (
                    <p className="mt-1 text-white">{profile?.[field.key] || formData?.[field.key] || "—"}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Certifications (for Contractors) */}
        {userRole === "contractor" && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              Certifications & Credentials
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">BBP Certification</span>
                  {profile?.bbp_certified ? (
                    <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full">Valid</span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full">Pending</span>
                  )}
                </div>
                <p className="text-sm text-slate-400">Bloodborne Pathogen Training</p>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">HIPAA Compliance</span>
                  {profile?.hipaa_certified ? (
                    <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full">Valid</span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full">Pending</span>
                  )}
                </div>
                <p className="text-sm text-slate-400">Healthcare Privacy Training</p>
              </div>
            </div>
          </div>
        )}

        {/* Telegram Connection */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            Telegram Integration
          </h3>
          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
            <div>
              <p className="font-medium">
                {profile?.telegram_chat_id ? "Connected to @askisaac_bot" : "Not Connected"}
              </p>
              <p className="text-sm text-slate-400">
                {profile?.telegram_chat_id 
                  ? "Receive real-time notifications via Telegram"
                  : "Connect to receive instant job alerts and updates"
                }
              </p>
            </div>
            {profile?.telegram_chat_id ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">Connected</span>
              </div>
            ) : (
              <Link to="/user-guides">
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                  Connect Now
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4">
          <Link 
            to="/account/settings"
            className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-cyan-500/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="font-medium group-hover:text-cyan-400 transition-colors">Account Settings</p>
                <p className="text-sm text-slate-400">Password, privacy & preferences</p>
              </div>
            </div>
          </Link>
          <Link 
            to="/telegram-walkthrough"
            className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-emerald-500/50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium group-hover:text-emerald-400 transition-colors">Telegram Guide</p>
                <p className="text-sm text-slate-400">Learn how to use @askisaac_bot</p>
              </div>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
}
